import React, { Suspense } from "react";
import { useRoutes } from "react-router-dom";
import Layout from "../Components/Layout/Layout";
import PrivateRoute from "./PrivateRoute";
import Root from "./Root";

// Lazy-loaded pages

// Auth
const LoginPage = React.lazy(() => import("../Pages/auth/Login"));
const Success = React.lazy(() => import("../Pages/auth/success"));

// Dashboard
const Dashboard = React.lazy(() => import("../Pages/dashboard/Dashboard"));
const CreateInfluencer = React.lazy(() =>
  import("../Pages/dashboard/CreateInfluencer")
);

// User Profile
const MyProfile = React.lazy(() => import("../Pages/profile/MyProfile"));

// Custom Media
const CustomMedia = React.lazy(() => import("../Pages/custom/CustomMedia"));

// Gallery
const Gallery = React.lazy(() => import("../Pages/gallery/Gallery"));

// Influencers
const Influencer = React.lazy(() => import("../Pages/influencer/Influencer"));
const Analytics = React.lazy(() => import("../Pages/influencer/Analytics"));

// Content
const Content = React.lazy(() => import("../Pages/content/Content"));
const EditContent = React.lazy(() => import("../Pages/content/EditContent"));
const DeleteContent = React.lazy(() => import("../Pages/content/DeleteContent"));

// Additional pages
const InfluencerName = React.lazy(() => import("../Pages/InfluencerName"));

// Error handlers
const PageNotFound = React.lazy(() =>
  import("../Pages/errorHandlers/NotFound")
);
const UnderMaintenance = React.lazy(() =>
  import("../Pages/errorHandlers/UnderMaintenance")
);

// Terms and privacy policy
const Terms = React.lazy(() => import("../Pages/termsAndPrivacy/Terms"));
const PrivacyPolicy = React.lazy(() =>
  import("../Pages/termsAndPrivacy/PrivacyPolicy")
);

// Instagram
const Instagram = React.lazy(() => import("../Pages/instagram/Instagram"));

// X -twitter
const X = React.lazy(() => import("../Pages/x/X"));
const XAuth = React.lazy(() => import("../Pages/x/XAuth"));

// TikTok
const TikTok = React.lazy(() => import("../Pages/tikTok/TikTok"));
const TikTokAuth = React.lazy(() => import("../Pages/tikTok/TikTokAuth"));

// Youtube
const Youtube = React.lazy(() => import("../Pages/youtube/Youtube"));
const YouTubeAuth = React.lazy(() => import("../Pages/youtube/YouTubeAuth"));

// Facebook
const Facebook = React.lazy(() => import("../Pages/facebook/Facebook"));
const FacebookAuth = React.lazy(() => import("../Pages/facebook/FacebookAuth"));

// Persona Management
const PersonaManagement = React.lazy(() =>
  import("../Pages/personaManagement/PersonaManagement")
);
const CreatePersona = React.lazy(() =>
  import("../Pages/personaManagement/CreatePersona")
);

// Loading fallback
const loading = () => (
  <div style={{ minHeight: "100vh" }} className="text-center">
    Loading....
  </div>
);

const AllRoutes = () => {
  const LoadComponent = ({ component: Component }) => (
    <Suspense fallback={loading}>
      <Component />
    </Suspense>
  );
  return useRoutes([
    { path: "/", element: <Root /> },
    {
      // Twitter OAuth callback (public route)
      path: "/xAuth",
      element: <LoadComponent component={XAuth} />,
    },
    {
      // Google OAuth callback (public route) - handles YouTube
      path: "/googleAuth",
      element: <LoadComponent component={YouTubeAuth} />,
    },
    {
      // TikTok OAuth callback (public route)
      path: "/tiktokAuth",
      element: <LoadComponent component={TikTokAuth} />,
    },
    {
      // Facebook OAuth callback (public route)
      path: "/facebookAuth",
      element: <LoadComponent component={FacebookAuth} />,
    },
    {
      // public routes
      path: "/",
      children: [
        { path: "login", element: <LoadComponent component={LoginPage} /> },
        { path: "success", element: <LoadComponent component={Success} /> },
        {
          path: "terms-of-service",
          element: <LoadComponent component={Terms} />,
        },
        {
          path: "privacy-policy",
          element: <LoadComponent component={PrivacyPolicy} />,
        },
        {
          path: "maintenance",
          element: <LoadComponent component={UnderMaintenance} />,
        },
      ],
    },
    {
      // auth protected routes
      path: "/",
      element: <PrivateRoute component={Layout} />,
      children: [
        {
          path: "dashboard",
          element: <LoadComponent component={Dashboard} />,
        },
        {
          path: "create-influencer",
          element: <LoadComponent component={CreateInfluencer} />,
        },
        {
          path: "influencer-name",
          element: <LoadComponent component={InfluencerName} />,
        },
        {
          path: "gallery",
          element: <LoadComponent component={Gallery} />,
        },
        {
          path: "media/:id",
          element: <LoadComponent component={Gallery} />,
        },
        {
          path: "my-profile",
          element: <LoadComponent component={MyProfile} />,
        },
        {
          path: "custom/:type",
          element: <LoadComponent component={CustomMedia} />,
        },
        {
          path: "create/:type/:id",
          element: <LoadComponent component={CustomMedia} />,
        },
        {
          path: "influencers",
          element: <LoadComponent component={Influencer} />,
        },
        {
          path: "analytics/:id",
          element: <LoadComponent component={Analytics} />,
        },
        {
          path: "content/:id",
          element: <LoadComponent component={Content} />,
        },
        {
          path: "content/edit/:id",
          element: <LoadComponent component={EditContent} />,
        },
        {
          path: "content/delete/:id",
          element: <LoadComponent component={DeleteContent} />,
        },
        {
          path: "instagram",
          element: <LoadComponent component={Instagram} />,
        },
        {
          path: "x",
          element: <LoadComponent component={X} />,
        },
        {
          path: "tikTok",
          element: <LoadComponent component={TikTok} />,
        },
        {
          path: "youtube",
          element: <LoadComponent component={Youtube} />,
        },
        {
          path: "facebook",
          element: <LoadComponent component={Facebook} />,
        },
        {
          path: "persona-management",
          element: <LoadComponent component={PersonaManagement} />,
        },
        {
          path: "create-persona",
          element: <LoadComponent component={CreatePersona} />,
        },
      ],
    },
    {
      // Catch all - must be last
      path: "*",
      element: <LoadComponent component={PageNotFound} />,
    },
  ]);
};

export { AllRoutes };
